import React, { useState, useContext, useMemo, useEffect } from 'react';
import * as Yup from 'yup';
import {
  Autocomplete,
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import allForm from '../../Stateless/Form';
import { HimmaContext } from '../../../context/HimmaContext';
import { useAllStores } from '../../Query/ManageStoreQuery';
import { COLORS } from '../../../theme';

const validationSchema = Yup.object().shape({
  reportType: Yup.string().required('Report Type is required'),
  fromDate: Yup.string().when('reportType', {
    is: 'custom',
    then: (schema) => schema.required('From Date is required for custom date range'),
    otherwise: (schema) => schema.nullable(),
  }),
  toDate: Yup.string().when('reportType', {
    is: 'custom',
    then: (schema) => schema.required('To Date is required for custom date range'),
    otherwise: (schema) => schema.nullable(),
  }),
  storeType: Yup.array().of(Yup.string()).nullable(),
  storeOwnerIds: Yup.array().of(Yup.string()).nullable(),
  storeIds: Yup.array().of(Yup.string()).nullable(),
  cancelledBy: Yup.array().of(Yup.string()).nullable(),
  cancellationReason: Yup.string().nullable(),
  orderStates: Yup.array().of(Yup.string()).nullable(),
  refundStatus: Yup.array().of(Yup.string()).nullable(),
});

const CancelledOrdersReport: React.FC = () => {
  const api = useContext(HimmaContext)!;
  const [selectedFromDate, setSelectedFromDate] = useState<Dayjs | null>(null);
  const [selectedToDate, setSelectedToDate] = useState<Dayjs | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [userType, setUserType] = useState<string>('');
  const [userId, setUserId] = useState<string>('');

  // Get user data from localStorage
  useEffect(() => {
    try {
      const userData = localStorage.getItem('userData');
      if (userData) {
        const parsedData = JSON.parse(userData);
        setUserType(parsedData?.userType || '');
        setUserId(parsedData?.userId || '');
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
    }
  }, []);

  const isMerchant = userType === 'merchant';

  // Fetch data from SDK
  const { data: storesData } = useAllStores({
    page: 1,
    limit: 100,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    statusFilter: 'All',
    addedByFilter: 'All',
  });

  // Filter stores for merchants - only show their own stores
  const storeOptions = useMemo(() => {
    let stores = storesData?.data || [];
    
    // If merchant, filter to only their stores
    if (isMerchant && userId) {
      stores = stores.filter((store: any) => store.merchantId === userId);
    }
    
    return stores.map((store: any) => ({
      value: store.id,
      label:
        store.restaurantDetails?.storeName ||
        store.nameEnglish ||
        store.name ||
        'Unknown Store',
      merchantId: store.merchantId,
    }));
  }, [storesData, isMerchant, userId]);


  const reportTypeOptions = [
    { value: 'today', label: 'Today' },
    { value: 'thisWeek', label: 'This Week' },
    { value: 'thisMonth', label: 'This Month' },
    { value: 'last30Days', label: 'Last 30 Days' },
    { value: 'thisYear', label: 'This Year' },
    { value: 'custom', label: 'Custom' },
  ];

  const storeTypeOptions = [
    { value: 'Restaurant', label: 'Food' },
    { value: 'GroceryStore', label: 'Grocery' },
    { value: 'PharmacyStore', label: 'Pharmacy' },
  ];

  const cancelledByOptions = [
    { value: 'customer', label: 'Customer' },
    { value: 'merchant', label: 'Merchant' },
    { value: 'deliveryPartner', label: 'Delivery Partner' },
    { value: 'admin', label: 'Admin' },
    { value: 'system', label: 'System' },
  ];

  const orderStatesOptions = [
    { value: 'ORDER_CANCELLED', label: 'Order Cancelled' },
    { value: 'MERCHANT_CANCELLED', label: 'Merchant Cancelled' },
    { value: 'DELIVERY_FAILED', label: 'Delivery Failed' },
    { value: 'MERCHANT_DENIED', label: 'Merchant Denied' },
  ];

  const refundStatusOptions = [
    { value: 'refunded', label: 'Refunded' },
    { value: 'notEligible', label: 'Not Eligible' },
    { value: 'initiated', label: 'Initiated' },
  ];

  const formConfig = {
    initialValues: {
      reportType: '',
      fromDate: '',
      toDate: '',
      storeType: [],
      storeOwnerIds: [],
      storeIds: [],
      cancelledBy: [],
      cancellationReason: '',
      orderStates: [], // Default to empty - will use API defaults
      refundStatus: [],
    },
    validationSchema,
    onSubmit: async (values: Record<string, any>) => {
      try {
        setIsLoading(true);
        setErrorMessage(null);
        setSuccessMessage(null);

        const {
          reportType,
          storeIds,
          storeOwnerIds,
          storeType,
          cancelledBy,
          cancellationReason,
          orderStates,
          refundStatus,
        } = values;

        const fromDate = selectedFromDate ? selectedFromDate?.toISOString().split('T')[0] + 'T00:00:00Z' : undefined;
        const toDate = selectedToDate ? selectedToDate?.toISOString().split('T')[0] + 'T23:59:59Z' : undefined;

        // Validate that reportType is selected
        if (!reportType || reportType.trim() === '') {
          setErrorMessage('Report Type is required');
          setIsLoading(false);
          return;
        }

        // Map UI report type to API dateRange enum
        const dateRange = reportType;

        // Build the request object according to CancelledOrdersReportRequest schema
        const cancelledOrdersReportRequest: any = {
          dateRange,
          orderStates:
            Array.isArray(orderStates) && orderStates.length > 0
              ? orderStates
              : ['ORDER_CANCELLED', 'MERCHANT_CANCELLED', 'DELIVERY_FAILED'], // Default
        };

        // Add optional filters
        if (Array.isArray(storeIds) && storeIds.length > 0) {
          cancelledOrdersReportRequest.storeIds = storeIds;
        }

        if (Array.isArray(storeOwnerIds) && storeOwnerIds.length > 0) {
          cancelledOrdersReportRequest.merchantId = storeOwnerIds[0]; // API accepts single merchantId
        }

        if (Array.isArray(storeType) && storeType.length > 0) {
          cancelledOrdersReportRequest.storeType = storeType;
        }

        if (Array.isArray(cancelledBy) && cancelledBy.length > 0) {
          cancelledOrdersReportRequest.cancelledBy = cancelledBy;
        }

        if (cancellationReason && cancellationReason.trim().length >= 2) {
          cancelledOrdersReportRequest.cancellationReason = cancellationReason.trim();
        }

        if (Array.isArray(refundStatus) && refundStatus.length > 0) {
          cancelledOrdersReportRequest.refundStatus = refundStatus;
        }

        // Add dateFrom and dateTo only if dateRange is 'custom'
        // IMPORTANT: Do not include dateFrom/dateTo for non-custom date ranges
        if (dateRange === 'custom') {
          if (!fromDate || !toDate) {
            setErrorMessage('Both From Date and To Date are required for custom date range');
            setIsLoading(false);
            return;
          }
          cancelledOrdersReportRequest.dateFrom = fromDate;
          cancelledOrdersReportRequest.dateTo = toDate;
        }
        // Explicitly ensure dateFrom and dateTo are NOT included for non-custom ranges
        // This prevents any accidental inclusion from form state

        // If merchant, automatically filter by their stores
        if (isMerchant && userId) {
          const merchantStoreIds = storeOptions.map((opt: any) => opt.value);
          if (merchantStoreIds.length > 0) {
            cancelledOrdersReportRequest.storeIds = merchantStoreIds;
          }
        }

        const response = await api.downloadCancelledOrdersReport(
          cancelledOrdersReportRequest,
          {
            responseType: 'blob' as any,
          }
        );

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `cancelled-orders-report-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setSuccessMessage('Report downloaded successfully!');
        setTimeout(() => setSuccessMessage(null), 5000);
      } catch (error: any) {
        const statusCode = error?.response?.status;
        let errorMsg = '';
        
        // Try to parse error message from different response formats
        if (error?.response?.data) {
          // When responseType is 'blob', error responses might be Blob objects
          if (error.response.data instanceof Blob) {
            // Convert blob to text and parse JSON
            try {
              const text = await error.response.data.text();
              const parsed = JSON.parse(text);
              errorMsg = parsed.message || parsed.error || text;
            } catch (parseError) {
              // If parsing fails, try to get text anyway
              try {
                errorMsg = await error.response.data.text();
              } catch {
                errorMsg = 'Failed to download cancelled orders report. Please try again.';
              }
            }
          } else if (typeof error.response.data === 'string') {
            // If it's already a string, try to parse as JSON
            try {
              const parsed = JSON.parse(error.response.data);
              errorMsg = parsed.message || parsed.error || error.response.data;
            } catch {
              errorMsg = error.response.data;
            }
          } else if (error.response.data.message) {
            // If it's already an object with message
            errorMsg = error.response.data.message;
          } else if (error.response.data.error) {
            errorMsg = error.response.data.error;
          }
        }
        
        // Fallback to default error message
        if (!errorMsg) {
          errorMsg = error?.message || 'Failed to download cancelled orders report. Please try again.';
        }
        
        // Check if it's a 404 with "No data" message - show as info instead of error
        if (
          statusCode === 404 &&
          (errorMsg?.toLowerCase().includes('no data') ||
           errorMsg?.toLowerCase().includes('no cancelled orders found') ||
           errorMsg?.toLowerCase().includes('not found') ||
           errorMsg?.toLowerCase().includes('no cancelled orders') ||
           errorMsg?.toLowerCase().includes('matching the selected filters'))
        ) {
          // Show as info message instead of error
          setInfoMessage(errorMsg || 'No cancelled orders found matching the selected filters and date range.');
          setTimeout(() => setInfoMessage(null), 5000);
        } else {
          // Show as error for other cases
          setErrorMessage(errorMsg);
        }
        console.error('Failed to download cancelled orders report', error);
      } finally {
        setIsLoading(false);
      }
    },
    validateOnChange: true,
    enableReinitialize: true,
    fields: [
      // Row 1: Report Type
      {
        name: 'reportType',
        label: 'Report Type:',
        type: 'select',
        options: reportTypeOptions,
        isRequired: true,
        component: ({ field, form }: any) => {
          // Clear date fields when switching away from custom
          const handleChange = (event: any) => {
            const newValue = event.target.value;
            form.setFieldValue(field.name, newValue);
            if (newValue !== 'custom') {
              form.setFieldValue('fromDate', '');
              form.setFieldValue('toDate', '');
              setSelectedFromDate(null);
              setSelectedToDate(null);
            }
          };
          
          return (
            <TextField
              {...field}
              select
              SelectProps={{
                native: true,
              }}
              onChange={handleChange}
              sx={{
                width: '100%',
                '& .MuiInputBase-root': {
                  fontSize: 14,
                  minHeight: 56,
                },
              }}
            >
              <option value="">Select Report Type</option>
              {reportTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </TextField>
          );
        },
      },
      // Row 2: From Date | To Date
      {
        name: 'fromDate',
        label: 'From Date:',
        type: 'custom',
        dependsOn: {
          field: 'reportType',
          value: 'custom',
        },
        component: ({ field, form }: any) => {
          const reportType = form.values.reportType;
          const isCustom = reportType === 'custom';
          const hasError = form.touched.fromDate && !!form.errors.fromDate;
          
          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={selectedFromDate}
                onChange={(newValue: Dayjs | null) => {
                  setSelectedFromDate(newValue);
                  form.setFieldValue(
                    field.name,
                    newValue ? newValue.toISOString() : ''
                  );
                }}
                maxDate={dayjs()}
                slotProps={{
                  textField: {
                    placeholder: 'Select start date',
                    helperText: hasError ? form.errors.fromDate : (isCustom ? 'Required for custom date range' : ''),
                    error: hasError,
                    required: isCustom,
                    onBlur: () => {
                      form.setFieldTouched(field.name, true);
                    },
                    sx: {
                      width: '100%',
                      '& .MuiInputBase-root': {
                        minHeight: 56,
                      },
                    },
                  },
                }}
                format="MM/DD/YYYY"
              />
            </LocalizationProvider>
          );
        },
      },
      {
        name: 'toDate',
        label: 'To Date:',
        type: 'custom',
        dependsOn: {
          field: 'reportType',
          value: 'custom',
        },
        component: ({ field, form }: any) => {
          const reportType = form.values.reportType;
          const isCustom = reportType === 'custom';
          const hasError = form.touched.toDate && !!form.errors.toDate;
          
          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={selectedToDate}
                onChange={(newValue: Dayjs | null) => {
                  setSelectedToDate(newValue);
                  form.setFieldValue(
                    field.name,
                    newValue ? newValue.toISOString() : ''
                  );
                }}
                maxDate={dayjs()}
                slotProps={{
                  textField: {
                    placeholder: 'Select end date',
                    helperText: hasError ? form.errors.toDate : (isCustom ? 'Required for custom date range' : ''),
                    error: hasError,
                    required: isCustom,
                    onBlur: () => {
                      form.setFieldTouched(field.name, true);
                    },
                    sx: {
                      width: '100%',
                      '& .MuiInputBase-root': {
                        minHeight: 56,
                      },
                    },
                  },
                }}
                format="MM/DD/YYYY"
              />
            </LocalizationProvider>
          );
        },
      },
      // Row 3: Store Type
      {
        name: 'storeType',
        label: 'Store Type:',
        type: 'custom',
        component: ({ field, form }: any) => {
          const selectedValues = storeTypeOptions.filter((opt: any) =>
            (field.value || []).includes(opt.value)
          );
          
          const handleChipDelete = (valueToDelete: string) => () => {
            const newValue = (field.value || []).filter((v: string) => v !== valueToDelete);
            form.setFieldValue(field.name, newValue);
          };

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Autocomplete
                multiple
                options={storeTypeOptions}
                getOptionLabel={(option) => option.label}
                value={selectedValues}
                onChange={(_, newValue) =>
                  form.setFieldValue(
                    field.name,
                    newValue.map((v: any) => v.value)
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select store types (optional)"
                    helperText="Leave empty to include all types"
                    sx={{
                      '& .MuiInputBase-root': {
                        fontSize: 14,
                        minHeight: 56,
                        width: '100%',
                      },
                    }}
                  />
                )}
                renderTags={() => []}
              />
              {selectedValues.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    mt: 1,
                  }}
                >
                  {selectedValues.map((option: any) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      onDelete={handleChipDelete(option.value)}
                      size="small"
                      sx={{ margin: '2px' }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          );
        },
      },
      // Row 4: Stores
      {
        name: 'storeIds',
        label: 'Stores:',
        type: 'custom',
        component: ({ field, form }: any) => {
          const selectedOwnerIds: string[] = form.values.storeOwnerIds || [];
          const filteredStoreOptions =
            Array.isArray(selectedOwnerIds) && selectedOwnerIds.length > 0
              ? storeOptions.filter((opt: any) =>
                  selectedOwnerIds.includes(opt.merchantId)
                )
              : storeOptions;

          const selectedValues = filteredStoreOptions.filter((opt: any) =>
            (field.value || []).includes(opt.value)
          );
          
          const handleChipDelete = (valueToDelete: string) => () => {
            const newValue = (field.value || []).filter((v: string) => v !== valueToDelete);
            form.setFieldValue(field.name, newValue);
          };

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Autocomplete
                multiple
                options={filteredStoreOptions}
                getOptionLabel={(option) => option.label}
                value={selectedValues}
                onChange={(_, newValue) =>
                  form.setFieldValue(
                    field.name,
                    newValue.map((v: any) => v.value)
                  )
                }
                disabled={isMerchant} // Merchants can't select stores - automatically filtered
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      isMerchant
                        ? 'All your stores (automatically included)'
                        : 'Select stores (optional)'
                    }
                    helperText={
                      isMerchant
                        ? 'All your stores are automatically included'
                        : 'Leave empty to include all stores'
                    }
                    sx={{
                      '& .MuiInputBase-root': {
                        fontSize: 14,
                        minHeight: 56,
                        width: '100%',
                      },
                    }}
                  />
                )}
                renderTags={() => []}
              />
              {selectedValues.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    mt: 1,
                  }}
                >
                  {selectedValues.map((option: any) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      onDelete={handleChipDelete(option.value)}
                      size="small"
                      sx={{ margin: '2px' }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          );
        },
      },
      // Row 5: Cancelled By | Cancellation Reason
      {
        name: 'cancelledBy',
        label: 'Cancelled By:',
        type: 'custom',
        component: ({ field, form }: any) => {
          const selectedValues = cancelledByOptions.filter((opt: any) =>
            (field.value || []).includes(opt.value)
          );
          
          const handleChipDelete = (valueToDelete: string) => () => {
            const newValue = (field.value || []).filter((v: string) => v !== valueToDelete);
            form.setFieldValue(field.name, newValue);
          };

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Autocomplete
                multiple
                options={cancelledByOptions}
                getOptionLabel={(option) => option.label}
                value={selectedValues}
                onChange={(_, newValue) =>
                  form.setFieldValue(
                    field.name,
                    newValue.map((v: any) => v.value)
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select who cancelled (optional)"
                    helperText="Leave empty to include all"
                    sx={{
                      '& .MuiInputBase-root': {
                        fontSize: 14,
                        minHeight: 56,
                        width: '100%',
                      },
                    }}
                  />
                )}
                renderTags={() => []}
              />
              {selectedValues.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    mt: 1,
                  }}
                >
                  {selectedValues.map((option: any) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      onDelete={handleChipDelete(option.value)}
                      size="small"
                      sx={{ margin: '2px' }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          );
        },
      },
      {
        name: 'cancellationReason',
        label: 'Cancellation Reason:',
        type: 'text',
        component: ({ field }: any) => (
          <TextField
            {...field}
            placeholder="Search by cancellation reason (optional)"
            helperText="Partial text search, case-insensitive"
            sx={{
              width: '100%',
              '& .MuiInputBase-root': {
                fontSize: 14,
                minHeight: 56,
              },
            }}
          />
        ),
      },
      // Row 6: Order States | Refund Status
      {
        name: 'orderStates',
        label: 'Order States:',
        type: 'custom',
        component: ({ field, form }: any) => {
          const selectedValues = orderStatesOptions.filter((opt: any) =>
            (field.value || []).includes(opt.value)
          );
          
          const handleChipDelete = (valueToDelete: string) => () => {
            const newValue = (field.value || []).filter((v: string) => v !== valueToDelete);
            form.setFieldValue(field.name, newValue);
          };

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Autocomplete
                multiple
                options={orderStatesOptions}
                getOptionLabel={(option) => option.label}
                value={selectedValues}
                onChange={(_, newValue) =>
                  form.setFieldValue(
                    field.name,
                    newValue.map((v: any) => v.value)
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select order states (optional)"
                    helperText="Leave empty to use default: Order Cancelled, Merchant Cancelled, Delivery Failed"
                    sx={{
                      '& .MuiInputBase-root': {
                        fontSize: 14,
                        minHeight: 56,
                        width: '100%',
                      },
                    }}
                  />
                )}
                renderTags={() => []}
              />
              {selectedValues.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    mt: 1,
                  }}
                >
                  {selectedValues.map((option: any) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      onDelete={handleChipDelete(option.value)}
                      size="small"
                      sx={{ margin: '2px' }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          );
        },
      },
      {
        name: 'refundStatus',
        label: 'Refund Status:',
        type: 'custom',
        component: ({ field, form }: any) => {
          const selectedValues = refundStatusOptions.filter((opt: any) =>
            (field.value || []).includes(opt.value)
          );
          
          const handleChipDelete = (valueToDelete: string) => () => {
            const newValue = (field.value || []).filter((v: string) => v !== valueToDelete);
            form.setFieldValue(field.name, newValue);
          };

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Autocomplete
                multiple
                options={refundStatusOptions}
                getOptionLabel={(option) => option.label}
                value={selectedValues}
                onChange={(_, newValue) =>
                  form.setFieldValue(
                    field.name,
                    newValue.map((v: any) => v.value)
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select refund status (optional)"
                    helperText="Leave empty to include all statuses"
                    sx={{
                      '& .MuiInputBase-root': {
                        fontSize: 14,
                        minHeight: 56,
                        width: '100%',
                      },
                    }}
                  />
                )}
                renderTags={() => []}
              />
              {selectedValues.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    mt: 1,
                  }}
                >
                  {selectedValues.map((option: any) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      onDelete={handleChipDelete(option.value)}
                      size="small"
                      sx={{ margin: '2px' }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          );
        },
      },
    ],
    customButton: () => (
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4, mb: 2 }}>
        <Button
          type="reset"
          variant="outlined"
          disabled={isLoading}
          sx={{
            borderColor: COLORS.secondary.main,
            color: COLORS.secondary.main,
            minWidth: 120,
            textTransform: 'none',
            fontSize: 16,
            fontWeight: 500,
            py: 1.5,
            '&:hover': {
              borderColor: COLORS.secondary.main,
              backgroundColor: alpha(COLORS.secondary.main, 0.1),
            },
          }}
        >
          Reset
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isLoading}
          sx={{
            backgroundColor: COLORS.primary.main,
            minWidth: 150,
            textTransform: 'none',
            fontSize: 16,
            fontWeight: 600,
            py: 1.5,
            '&:hover': {
              backgroundColor: alpha(COLORS.primary.dark, 0.8),
            },
            '&:disabled': {
              backgroundColor: alpha(COLORS.primary.main, 0.5),
            },
          }}
        >
          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} sx={{ color: 'white' }} />
              Generating...
            </Box>
          ) : (
            'Download Report'
          )}
        </Button>
      </Box>
    ),
  };

  return (
    <Box
      sx={{
        backgroundColor: '#f5f5f5',
        minHeight: '100vh',
        padding: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Box sx={{ maxWidth: 1400, margin: '0 auto' }}>
      <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            marginBottom: 1,
            color: '#1a1a1a',
            fontSize: { xs: '1.75rem', sm: '2rem' },
          }}
      >
        Cancelled Orders Report
      </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#666',
            marginBottom: 3,
            fontSize: '0.95rem',
          }}
        >
          Generate and download detailed cancelled orders reports with advanced filtering options.
          {isMerchant && ' You can only access cancelled orders from your own stores.'}
        </Typography>

        {(errorMessage || successMessage || infoMessage) && (
          <Box sx={{ mb: 3 }}>
            {errorMessage && (
              <Alert
                severity="error"
                onClose={() => setErrorMessage(null)}
                sx={{ mb: 2 }}
              >
                {errorMessage}
              </Alert>
            )}
            {successMessage && (
              <Alert
                severity="success"
                onClose={() => setSuccessMessage(null)}
                sx={{ mb: 2 }}
              >
                {successMessage}
              </Alert>
            )}
            {infoMessage && (
              <Alert
                severity="info"
                onClose={() => setInfoMessage(null)}
                sx={{ mb: 2 }}
              >
                {infoMessage}
              </Alert>
            )}
          </Box>
        )}

        <Paper
          elevation={2}
          sx={{
            padding: { xs: 2, sm: 3, md: 4 },
            borderRadius: 2,
            backgroundColor: 'white',
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#333',
                mb: 1,
                fontSize: '1.1rem',
              }}
            >
              Report Filters
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
              Configure your report parameters below. Fields marked with * are required.
              The report filters by CANCEL DATE (when order was cancelled), not by order creation date.
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

        {allForm(formConfig)()}
        </Paper>
      </Box>
    </Box>
  );
};

export default CancelledOrdersReport;
